import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Sparkles, Star, Calendar, Gamepad2, Building2, Tag, Loader2, CheckCircle2, ImageIcon } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

export default function RawgImportModal({ open, onClose, onImported, categories = [] }) {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [importing, setImporting] = useState(false);
  const [config, setConfig] = useState({
    price: 59.90,
    discount_percent: 0,
    stock: 999,
    category: "",
    featured: false,
    trending: false,
    flash_deal: false,
    new_release: false,
    top_seller: false,
  });
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setSelected(null);
      setDetail(null);
    }
  }, [open]);

  // Debounced live search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/admin/rawg/search?q=${encodeURIComponent(query)}&page_size=12`);
        setResults(data.results || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setSearching(false);
      }
    }, 380);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, open]);

  const onSelect = async (game) => {
    setSelected(game);
    setDetail(null);
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/admin/rawg/games/${game.rawg_id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingDetail(false);
    }
  };

  const onImport = async () => {
    if (!selected) return;
    setImporting(true);
    try {
      const payload = { rawg_id: selected.rawg_id, ...config, price: parseFloat(config.price), discount_percent: parseFloat(config.discount_percent), stock: parseInt(config.stock) };
      const { data } = await api.post("/admin/rawg/import", payload);
      toast.success(`Imported "${data.title}" successfully!`);
      onImported?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-sm p-4"
        onClick={onClose}
        data-testid="rawg-import-modal"
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0 }}
          transition={{ type: "spring", damping: 24, stiffness: 220 }}
          className="w-full max-w-6xl rounded-3xl bg-[#171723] border border-white/10 shadow-[0_24px_80px_rgba(168,85,247,0.25)] max-h-[92vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-5 border-b border-white/5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#A855F7] to-[#7E22CE] grid place-items-center shadow-neon-soft">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-bold text-white">Import from RAWG</h2>
              <p className="text-xs text-slate-400">Search 850k+ games and auto-populate metadata</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white" data-testid="rawg-modal-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[420px_1fr]">
            {/* Left: search + results */}
            <div className="border-r border-white/5 flex flex-col overflow-hidden">
              <div className="p-5">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search any game (e.g. Spider-Man, FIFA, GTA)…"
                    className="w-full pl-10 pr-10 h-11 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#A855F7]"
                    data-testid="rawg-search-input"
                  />
                  {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A855F7] animate-spin" />}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-2" data-testid="rawg-results">
                {!query.trim() && !results.length && (
                  <div className="text-center py-16 px-6">
                    <Gamepad2 className="w-10 h-10 text-[#A855F7]/40 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">Type to search games from RAWG</p>
                  </div>
                )}
                {!searching && query.trim() && !results.length && (
                  <div className="text-center py-16 text-sm text-slate-500">No games found</div>
                )}
                {results.map((g, i) => (
                  <motion.button
                    key={g.rawg_id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    onClick={() => onSelect(g)}
                    className={`w-full text-left p-2 rounded-xl flex items-center gap-3 transition-colors ${
                      selected?.rawg_id === g.rawg_id ? "bg-[#A855F7]/15 border border-[#A855F7]/30" : "hover:bg-white/5 border border-transparent"
                    }`}
                    data-testid={`rawg-result-${g.rawg_id}`}
                  >
                    {g.background_image ? (
                      <img src={g.background_image} alt={g.name} className="w-14 h-14 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-white/5 grid place-items-center shrink-0"><ImageIcon className="w-5 h-5 text-slate-500" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-sm text-white truncate">{g.name}</div>
                      <div className="text-xs text-slate-400 flex items-center gap-2 mt-0.5">
                        {g.released && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{g.released?.slice(0, 4)}</span>}
                        {g.rating > 0 && <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />{g.rating.toFixed(1)}</span>}
                      </div>
                      {g.platforms?.length > 0 && (
                        <div className="text-[10px] text-slate-500 mt-0.5 truncate">{g.platforms.slice(0, 3).join(" • ")}</div>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Right: preview + import config */}
            <div className="overflow-y-auto">
              {!selected && (
                <div className="h-full grid place-items-center p-12 text-center">
                  <div>
                    <Sparkles className="w-12 h-12 text-[#A855F7]/30 mx-auto mb-4" />
                    <h3 className="font-display text-lg font-bold text-white">Select a game to preview</h3>
                    <p className="text-sm text-slate-500 mt-1 max-w-sm">Cover, screenshots, description, platforms and metadata will load here.</p>
                  </div>
                </div>
              )}

              {selected && loadingDetail && (
                <div className="p-8 space-y-4">
                  <div className="aspect-video rounded-2xl bg-white/5 animate-pulse" />
                  <div className="h-6 rounded bg-white/5 animate-pulse w-2/3" />
                  <div className="h-4 rounded bg-white/5 animate-pulse" />
                  <div className="h-4 rounded bg-white/5 animate-pulse w-5/6" />
                </div>
              )}

              {detail && !loadingDetail && (
                <div data-testid="rawg-detail">
                  {/* Hero cover */}
                  <div className="relative aspect-[16/7] overflow-hidden">
                    {detail.background_image ? (
                      <img src={detail.background_image} alt={detail.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#A855F7]/30 to-[#7E22CE]/30" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#171723] via-[#171723]/40 to-transparent" />
                    <div className="absolute bottom-4 left-6 right-6">
                      <h3 className="font-display text-2xl lg:text-3xl font-bold text-white drop-shadow">{detail.name}</h3>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-300">
                        {detail.released && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{detail.released}</span>}
                        {detail.rating > 0 && <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />{detail.rating.toFixed(2)} ({detail.rating_count})</span>}
                        {detail.metacritic && <span className="px-1.5 rounded bg-emerald-500/20 text-emerald-300 font-semibold">{detail.metacritic}</span>}
                        {detail.esrb_rating && <span className="px-1.5 rounded bg-white/10">{detail.esrb_rating}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-5">
                    {/* Description */}
                    <p className="text-sm text-slate-300 leading-relaxed line-clamp-4">{detail.description || "—"}</p>

                    {/* Metadata grid */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <Meta label="Platforms" value={detail.platforms?.join(", ") || "—"} />
                      <Meta label="Genres" value={detail.genres?.join(", ") || "—"} />
                      <Meta label="Publishers" value={detail.publishers?.join(", ") || "—"} />
                      <Meta label="Developers" value={detail.developers?.join(", ") || "—"} />
                    </div>

                    {/* Screenshots */}
                    {detail.screenshots?.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-slate-400 uppercase mb-2 tracking-wider">Screenshots ({detail.screenshots.length})</div>
                        <div className="grid grid-cols-3 gap-2">
                          {detail.screenshots.slice(0, 6).map((s, i) => (
                            <img key={i} src={s} alt="" className="w-full aspect-video rounded-lg object-cover" />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Import config */}
                    <div className="pt-4 border-t border-white/10">
                      <div className="text-xs font-semibold text-slate-400 uppercase mb-3 tracking-wider">Configure import</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <NumField label="Price (R$)" value={config.price} onChange={(v) => setConfig({ ...config, price: v })} step="0.01" testid="rawg-price" />
                        <NumField label="Discount %" value={config.discount_percent} onChange={(v) => setConfig({ ...config, discount_percent: v })} testid="rawg-discount" />
                        <NumField label="Stock" value={config.stock} onChange={(v) => setConfig({ ...config, stock: v })} testid="rawg-stock" />
                        <label className="block sm:col-span-3">
                          <div className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Category</div>
                          <select value={config.category} onChange={(e) => setConfig({ ...config, category: e.target.value })} className="w-full h-9 px-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" data-testid="rawg-category">
                            <option value="" className="bg-[#171723]">Auto-detect from platforms</option>
                            {categories.map((c) => <option key={c.id} value={c.name} className="bg-[#171723]">{c.name}</option>)}
                          </select>
                        </label>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4">
                        {[["featured", "Featured"], ["trending", "Trending"], ["flash_deal", "Flash deal"], ["new_release", "New release"], ["top_seller", "Top seller"]].map(([k, l]) => (
                          <label key={k} className="flex items-center gap-2 text-xs text-slate-300">
                            <input type="checkbox" checked={config[k]} onChange={(e) => setConfig({ ...config, [k]: e.target.checked })} className="accent-[#A855F7]" data-testid={`rawg-flag-${k}`} />
                            {l}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-white/5 flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              {detail ? <>Importing as <span className="text-[#C084FC] font-semibold">{config.category || "auto-detect"}</span></> : "Select a game to enable import"}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="border-white/10 text-slate-300 hover:bg-white/5">Cancel</Button>
              <Button onClick={onImport} disabled={!detail || importing} className="bg-[#A855F7] hover:bg-[#9333EA]" data-testid="rawg-import-confirm">
                {importing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Import to catalog</>}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-slate-500 uppercase font-semibold mb-0.5">{label}</div>
      <div className="text-slate-200 line-clamp-2">{value}</div>
    </div>
  );
}

function NumField({ label, value, onChange, step = "1", testid }) {
  return (
    <label className="block">
      <div className="text-[10px] text-slate-500 uppercase font-semibold mb-1">{label}</div>
      <input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-9 px-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-[#A855F7] focus:outline-none" data-testid={testid} />
    </label>
  );
}
